/**
 * Firebase Configuration and Initialization
 *
 * SETUP INSTRUCTIONS:
 * 1. Go to Firebase Console: https://console.firebase.google.com/
 * 2. Create a new project or select existing
 * 3. Go to Project Settings > General > Your Apps
 * 4. Add a web app and copy the config
 * 5. Replace the config below with your actual Firebase config
 * 6. Create a .env file with your config (recommended for production)
 */

import { initializeApp } from 'firebase/app';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { getMessaging, isSupported as isMessagingSupported, type Messaging } from 'firebase/messaging';

// Firebase configuration
// Replace with your actual Firebase config
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "YOUR_API_KEY",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "your-project.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "your-project-id",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "your-project.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:abcdef"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// ────────────────────────────────────────────────────────────────────────────
// Firestore client with offline persistence
// ────────────────────────────────────────────────────────────────────────────
//
// Uses IndexedDB to cache every read locally. Effects:
//
//   - Second+ visits: listeners hit the cache immediately and the dashboard
//     renders fast, then Firestore syncs deltas from the server in the
//     background. Removes the "/home mounts 10 listeners and waits 3 seconds
//     for them all to fetch" pause that everyone notices after sign-in.
//   - Offline reads: cached data is still readable when the browser has no
//     network. Combined with the existing public/sw.js (app shell cache)
//     and offline-queue.service.ts (explicit write queue) this gives THEO
//     a real PWA-grade offline mode.
//   - Offline writes: the Firestore SDK queues mutations automatically and
//     replays them when the network returns. Coexists with the custom
//     idb-keyval offline queue — they handle different write categories
//     (SDK queue = any addDoc/updateDoc, custom queue = explicit action
//     types with retry metadata).
//   - Cost reduction: repeat reads no longer hit the network.
//
// persistentMultipleTabManager lets every browser tab share the same
// IndexedDB store. Without this, only one tab gets the persistence "lock"
// and the rest fall back to memory cache — fine for normal users, painful
// for admins who run multiple THEO tabs side by side.
//
// memoryLocalCache is the fallback for environments where IndexedDB is
// unavailable: Safari private browsing in older iOS versions, quota
// exhaustion, certain locked-down corporate browsers, etc. The app still
// works in this case — it just doesn't get the second-visit speedup.
//
// IMPORTANT: initializeFirestore MUST be called BEFORE any other module
// calls getFirestore(app). Vite's module resolution makes this file the
// first import in nearly every code path that touches Firestore, so the
// ordering holds as long as no service file calls getFirestore directly.
// Verified at write time — only this file initializes Firestore.
// ────────────────────────────────────────────────────────────────────────────

let firestoreCache;
try {
  firestoreCache = persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  });
} catch (err) {
  // Constructor itself rarely throws; the real failure (IndexedDB blocked,
  // schema upgrade conflict, quota) surfaces later as a runtime warning
  // from the SDK. This catch is the belt — fallback is the braces.
  console.warn(
    '[firebase] Persistent Firestore cache unavailable, falling back to memory cache:',
    err,
  );
  firestoreCache = memoryLocalCache();
}

export const db = initializeFirestore(app, {
  localCache: firestoreCache,

  // ──────────────────────────────────────────────────────────────────────────
  // Ignore `undefined` field values on writes. Matches what the server has
  // done since Phase 2.0 (`functions/src/index.ts` — same one-liner, same
  // reason), and closes the single most destructive write bug in this repo.
  //
  // Without it, ONE undefined anywhere in a payload — including nested inside
  // an array of objects, which no top-level guard catches — makes Firestore
  // reject the ENTIRE write. Not the field: the whole set/update/batch. And
  // because these writes usually sit inside a try/catch that logs and carries
  // on, the failure is invisible: the UI has already moved on with its
  // in-memory copy, so everything looks saved to the person who typed it.
  //
  // Two live examples, both August 2026:
  //   - `usedMaterials[i].adjustmentReason: undefined` (a tech ticking a part
  //     at its planned quantity) sank every install completion write — note,
  //     gear, site vault, tagged field log — and the office opened the deal to
  //     find nothing from the visit.
  //   - `returnVisitContext.previousFieldLog: undefined` (a job with no field
  //     log) made `batch.set()` throw before commit, so return visits created
  //     no follow-up job, no deal sync and no action item. Recorded on
  //     jobs/70ZJdnrf4zzFuXpuVN51.
  //
  // `stripUndefinedDeep()` remains the house style and stays where it is — it
  // documents intent at the call site and keeps the payload honest in tests.
  // But it was only reachable by remembering it, in 32 of the ~150 files that
  // write, across ~570 call sites, and 8 of the 19 hand-rolled copies of that
  // helper only walk the top level. This makes forgetting it survivable.
  //
  // Deliberately NOT a licence to write undefined: an omitted field still
  // means "leave it alone", and deleting a field still requires deleteField().
  // Pinned by src/__tests__/firestore-write-safety.test.ts.
  // ──────────────────────────────────────────────────────────────────────────
  ignoreUndefinedProperties: true,
});

// ────────────────────────────────────────────────────────────────────────────
// Local Firestore emulator (opt-in, dev only)
//
// Set VITE_USE_FIRESTORE_EMULATOR=1 in .env to point the app at a local
// `firebase emulators:start --only firestore` instance — used for UI audits
// and offline development against seeded fixture data. Never enabled in
// production builds (the flag is absent and the guard also requires DEV).
// ────────────────────────────────────────────────────────────────────────────
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIRESTORE_EMULATOR === '1') {
  const { connectFirestoreEmulator } = await import('firebase/firestore');
  const host = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || '127.0.0.1';
  const port = Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8080);
  connectFirestoreEmulator(db, host, port);
  console.info(`[firebase] Using LOCAL Firestore emulator at ${host}:${port}`);
}

// Initialize Auth
export const auth = getAuth(app);

// ────────────────────────────────────────────────────────────────────────────
// Local Auth emulator + anonymous session (opt-in, dev only)
//
// The AuthGuard treats "localStorage session present but Firebase Auth has no
// user" as a stale session and forces re-login. In emulator audit mode there
// is no Cloud Function PIN exchange to mint a real user, so we sign in
// anonymously against the local Auth emulator BEFORE the app renders (top-
// level await holds every importer until the session exists). Same flag as
// the Firestore emulator — one switch drives the whole local audit stack.
// ────────────────────────────────────────────────────────────────────────────
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIRESTORE_EMULATOR === '1') {
  try {
    const { connectAuthEmulator, signInAnonymously } = await import('firebase/auth');
    const host = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || '127.0.0.1';
    const port = Number(import.meta.env.VITE_AUTH_EMULATOR_PORT || 9099);
    connectAuthEmulator(auth, `http://${host}:${port}`, { disableWarnings: true });
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }
    console.info(`[firebase] Using LOCAL Auth emulator at ${host}:${port} (anonymous session)`);
  } catch (err) {
    console.warn('[firebase] Auth emulator sign-in failed (AuthGuard may bounce):', err);
  }
}

// Initialize Cloud Functions
export const functions = getFunctions(app);

// Same emulator switch again: in e2e mode callables go to the local functions
// emulator port. The e2e stack doesn't run one, so a callable fails fast
// (connection refused) instead of hanging on the production URL — the ETA
// sheet, PIN re-auth and the autobuild decision all surface their error
// state within a second rather than spinning until a network timeout.
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIRESTORE_EMULATOR === '1') {
  const { connectFunctionsEmulator } = await import('firebase/functions');
  const host = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || '127.0.0.1';
  const port = Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT || 5001);
  connectFunctionsEmulator(functions, host, port);
  console.info(`[firebase] Callables → LOCAL functions emulator at ${host}:${port}`);
}

// Initialize Firebase Storage (for PDF uploads, design imports, etc.)
export const storage = getStorage(app);

// Same emulator switch as Firestore + Auth above: in e2e mode uploads
// (bug-report screenshots, photos) land in the local Storage emulator.
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIRESTORE_EMULATOR === '1') {
  const { connectStorageEmulator } = await import('firebase/storage');
  const host = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || '127.0.0.1';
  const port = Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT || 9199);
  connectStorageEmulator(storage, host, port);
  console.info(`[firebase] Using LOCAL Storage emulator at ${host}:${port}`);
}

// Initialize Firebase Cloud Messaging (push notifications)
// Messaging is not supported in all browsers (e.g. Firefox private, some iOS browsers)
// so we lazy-init and export a promise that resolves to Messaging | null.
export const messagingPromise: Promise<Messaging | null> = isMessagingSupported()
  .then((supported) => (supported ? getMessaging(app) : null))
  .catch(() => null);

// Connect to emulator in development (uncomment when testing locally)
// import { connectFunctionsEmulator } from 'firebase/functions';
// if (import.meta.env.DEV) {
//   connectFunctionsEmulator(functions, 'localhost', 5001);
// }

export default app;
